import { ArrowDownRight, ArrowUpRight, ShieldAlert } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../ui/tooltip";

export type TagInfoMap = Record<string, string>;

const normalizeTagKey = (value: string) => value.replace(/^[+\-]\s*/, "").trim().toLowerCase();

const getTagInfo = (tag: string, tagInfo?: TagInfoMap) => {
    if (!tagInfo) return "";
    if (tagInfo[tag]) return tagInfo[tag];

    const normalizedTag = normalizeTagKey(tag);
    const match = Object.entries(tagInfo).find(([key]) => normalizeTagKey(key) === normalizedTag);
    return match?.[1] || "";
};

interface RiskTagBadgeProps {
    tag: string;
    tagInfo?: TagInfoMap;
}

export function RiskTagBadge({ tag, tagInfo }: RiskTagBadgeProps) {
    const info = getTagInfo(tag, tagInfo);
    const isRemoved = /^[\s]*[-−]/.test(tag);
    const badge = (
        <span className={`cs-card-tag ${isRemoved ? "removed" : "added"} cursor-default`}>
            {isRemoved ? <ArrowDownRight className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />} {tag}
        </span>
    );

    if (!info) return badge;

    return (
        <TooltipProvider delayDuration={0} skipDelayDuration={0}>
            <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>{badge}</TooltipTrigger>
                <TooltipContent
                    side="top"
                    sideOffset={8}
                    className="max-w-[320px] rounded-lg border border-border/70 bg-popover px-3 py-2 text-left shadow-xl"
                >
                    <div className="text-[11px] font-semibold text-foreground">{tag}</div>
                    <div className="mt-1 text-[12px] leading-snug text-muted-foreground">{info}</div>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

export function TotalAlertsBadge({ count }: { count?: number }) {
    const alertCount = Number(count) || 0;
    if (alertCount <= 0) return null;

    return (
        <div
            className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40"
            title={`${alertCount} total alert${alertCount === 1 ? "" : "s"}`}
        >
            <ShieldAlert className="w-3.5 h-3.5 text-red-500" />
            <span className="text-xs font-semibold text-red-600 dark:text-red-400">{alertCount}</span>
        </div>
    );
}
